import React, { useState } from "react"
import { Box, Modal, Typography, IconButton } from "@mui/material"
import { Close as CloseIcon } from '@mui/icons-material'
import { schemas } from "../generated/api/client"
import { z } from "zod"
import IconButtonWithTooltip, { IconButtonWithTooltipProps } from "./IconButtonWithTooltip"
import SingleClickWaitingButton from "./SingleClickWaitingButton"
import { apiClient } from "../bootstrapping/InitApiClient"

type EBulkApplication = z.infer<typeof schemas.EBulkApplication>

const getLabelForEResultKey = (key: string) => {
  switch (key) {
    case 'disclosureType':
      return 'Disclosure Type'
    case 'disclosureNumber':
      return 'Disclosure Number'
    case 'disclosureStatus':
      return 'Disclosure Status'
    case 'disclosureIssueDate':
      return 'Disclosure Issue Date'
    default:
      return key
  }
}

const EResultIconButton: React.FC<{ dbsApp: EBulkApplication } & Omit<IconButtonWithTooltipProps, 'onclick'>> = ({ dbsApp, ...iconButtonWithProps }) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 750,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  return dbsApp.dbs_eresult != null
    ? <>
      <IconButtonWithTooltip {...iconButtonWithProps} onClick={handleOpen} />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
            Application E-Result
          </Typography>
          <Typography component="body" variant="body1" sx={{ mb: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 2 }}>
              {Object.entries(dbsApp.dbs_eresult).map(([key, value]) => (
                <React.Fragment key={key}>
                  <Typography variant="subtitle1" component="div">{getLabelForEResultKey(key)}</Typography>
                  <Typography variant="body1" component="div">{`${value}`}</Typography>
                </React.Fragment>
              ))}
            </Box>
          </Typography>
          <SingleClickWaitingButton
            variant="contained"
            color="primary"
            onClick={async () => {
              try {
                // 1. fetch the PDF as a blob  
                const blob = await apiClient.getEBulkApplicationEResultPDF({
                  params: { id: dbsApp.id },
                  responseType: "blob"
                }) as unknown as Blob // NOTE: This is needed because non-JSON responses are considered "void" by the `openapi-zod-client` generator, but this is a Blob 

                // Check that the variable blob is a Blob, because we've cast it to a Blob
                if (!(blob instanceof Blob)) {
                  throw new Error("Response is not a Blob")
                }

                // 2. turn it into a downloadable URL  
                const url = window.URL.createObjectURL(blob)

                // 3. create a temporary <a> to trigger the download  
                const link = document.createElement("a")
                link.href = url
                link.download = `eresult-${dbsApp.id}.pdf`
                document.body.appendChild(link)
                link.click()

                // 4. clean up  
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
              } catch (err) {
                console.error("Download failed", err)
              }
            }}
            sx={{ mt: 2 }}
          >
            Download PDF
          </SingleClickWaitingButton>
        </Box>
      </Modal>
    </>
    : null
}

export default EResultIconButton
